import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { WebSocketService, WebSocketMessage } from './websocket.service';
import { tap } from 'rxjs/operators';

export interface RemoteCaptureData {
  sessionId: string;
  computerId: string;
  fieldId: string;
  fileName: string;
  size: number;
  mimeType: string;
  base64Data: string;
  timestamp: Date;
}

export interface SessionStatus {
  active: boolean;
  message?: string;
  fields?: RemoteField[];
  sessionId?: string;
}

export interface RemoteField {
  id: string;
  label: string;
  description?: string;
  accept: string;
  multiple: boolean;
  maxFiles?: number;
  maxSize?: number;
  required: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RemoteCaptureService {
  private activeSessions = new Map<string, BehaviorSubject<RemoteCaptureData | null>>();
  private sessionStatus = new Map<string, BehaviorSubject<SessionStatus>>();
  private readonly STORAGE_PREFIX = 'remote_capture_session_';

  constructor(private webSocketService: WebSocketService) {

    // Solo inicializar si WebSocket está habilitado
    if (!environment.websocket?.enabled) {
      return;
    }

    // Escuchar cambios en localStorage como fallback
    window.addEventListener('storage', (event) => {
      if (event.key && event.key.startsWith(this.STORAGE_PREFIX)) {
        this.handleStorageChange(event);
      }
    });

    // Detectar cuando se cierra la ventana del navegador
    window.addEventListener('beforeunload', () => {
      // Notificar a todos los clientes que la sesión se cerró
      this.sessionStatus.forEach((statusSubject, sessionId) => {
        if (statusSubject.value.active) {
          this.endSession(sessionId);
        }
      });
    });

            // Suscribirse a mensajes WebSocket cuando se conecte
    this.webSocketService.getConnectionStatusObservable().subscribe((isConnected) => {
      if (isConnected) {
        // LIMPIAR TODAS LAS SESIONES al conectar (evitar estado obsoleto)
        this.sessionStatus.forEach((statusSubject, sessionId) => {
          statusSubject.next({
            active: false,
            message: 'Esperando nueva sesión...',
            sessionId: sessionId
          });
        });

        // SOLICITAR SESIONES ACTIVAS al conectar
        this.webSocketService.requestActiveSessions('mobile');

        // Suscribirse a mensajes de estado de sesión
        this.webSocketService.getSessionStatusObservable().subscribe((status) => {
          if (status && status.sessionId) {
             // Actualizar la sesión específica si existe
             const statusSubject = this.sessionStatus.get(status.sessionId);
             if (statusSubject) {
               statusSubject.next(status);
             } else {
               // Crear la sesión local si no existe
               const newStatusSubject = new BehaviorSubject<SessionStatus>(status);
               this.sessionStatus.set(status.sessionId, newStatusSubject);
               // Emitir inmediatamente el estado actual
               newStatusSubject.next(status);
             }
           }
         });
       }
     });

    this.webSocketService.getFileDataObservable().subscribe((fileData) => {
      if (fileData) {
        // Buscar la sesión correspondiente y enviar el archivo
        const session = this.activeSessions.get(fileData.sessionId);
        if (session) {
          session.next(fileData);
        }
      }
    });
  }

  private handleStorageChange(event: StorageEvent) {
    const sessionId = event.key?.replace(this.STORAGE_PREFIX, '');
    if (sessionId && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        if (data.type === 'status') {
          const statusSession = this.sessionStatus.get(sessionId);
          if (statusSession) {
            statusSession.next(data.status);
          }
        } else if (data.type === 'file') {
          const fileSession = this.activeSessions.get(sessionId);
          if (fileSession) {
            fileSession.next(data.fileData);
          }
        }
      } catch (error) {
        // Error parsing storage data
      }
    }
  }

    // Crear una sesión activa
  createSession(sessionId: string, fields?: RemoteField[]): void {
    // Conectar WebSocket si es necesario
    this.webSocketService.connectIfNeeded();

    // SOLO usar WebSocket - no localStorage
    const isConnected = this.webSocketService.isConnected();

    if (isConnected) {
      this.webSocketService.createSession(sessionId, 'computer', fields);
    } else {
      return; // No crear sesión si no hay WebSocket
    }

    // Crear observable de estado de sesión (sin localStorage)
    if (!this.sessionStatus.has(sessionId)) {
      const statusSubject = new BehaviorSubject<SessionStatus>({
        active: true,
        message: 'Sesión activa',
        fields: fields
      });
      this.sessionStatus.set(sessionId, statusSubject);
    } else {
      // Actualizar campos si la sesión ya existe
      const existingStatus = this.sessionStatus.get(sessionId);
      if (existingStatus) {
        const newStatus = {
          active: true,
          message: 'Sesión activa',
          fields: fields
        };
        existingStatus.next(newStatus);
      }
    }
  }

  // Obtener observable para una sesión
  getSessionObservable(sessionId: string): Observable<RemoteCaptureData | null> | null {
    // Si WebSocket no está habilitado, retornar null
    if (!environment.websocket?.enabled) {
      return null;
    }

    const session = this.activeSessions.get(sessionId);
    if (session) {
      return session.asObservable();
    } else {
      return null;
    }
  }

  // Obtener observable de estado de sesión
  getSessionStatusObservable(sessionId: string): BehaviorSubject<SessionStatus> | null {
    let statusSubject = this.sessionStatus.get(sessionId);

    if (!statusSubject) {
      // Crear un observable por defecto si no existe
      statusSubject = new BehaviorSubject<SessionStatus>({
        active: false,
        message: 'Esperando sesión...',
        sessionId: sessionId
      });
      this.sessionStatus.set(sessionId, statusSubject);
    }

    return statusSubject;
  }

  // Obtener observable de respuestas de validación
  getValidationResponseObservable(): Observable<any> {
    return this.webSocketService.getValidationResponseObservable();
  }

  // Obtener observable de estado de conexión WebSocket
  getConnectionStatusObservable(): Observable<boolean> {
    return this.webSocketService.getConnectionStatusObservable().pipe(
      tap(isConnected => {
        if (isConnected) {
          // SOLICITAR SESIONES ACTIVAS al conectar
          this.webSocketService.requestActiveSessions('mobile');
        }
      })
    );
  }

  // Forzar conexión WebSocket
  connectWebSocket(): void {
    if (!environment.websocket?.enabled) {
      return;
    }

    this.webSocketService.connectIfNeeded();
  }

  // Verificar si una sesión está activa
  isSessionActive(sessionId: string): boolean {
    // Si WebSocket no está habilitado, siempre retornar false
    if (!environment.websocket?.enabled) {
      return false;
    }

    const storageKey = this.STORAGE_PREFIX + sessionId;
    const sessionData = localStorage.getItem(storageKey);

    if (sessionData) {
      try {
        const data = JSON.parse(sessionData);
        return data.status && data.status.active;
      } catch (error) {
        // Error parsing session data
      }
    }

    return this.activeSessions.has(sessionId);
  }

  // Verificar si una sesión está disponible inmediatamente
  isSessionAvailable(sessionId: string): boolean {
    return this.sessionStatus.has(sessionId);
  }

  // Obtener el estado actual de una sesión
  getCurrentSessionStatus(sessionId: string): SessionStatus | null {
    // Si WebSocket no está habilitado, retornar null
    if (!environment.websocket?.enabled) {
      return null;
    }

    const storageKey = this.STORAGE_PREFIX + sessionId;
    const sessionData = localStorage.getItem(storageKey);

    if (sessionData) {
      try {
        const data = JSON.parse(sessionData);
        if (data.status) {
          return data.status;
        }
      } catch (error) {
        // Error parsing session data
      }
    }

    // Verificar en BehaviorSubject
    const statusSubject = this.sessionStatus.get(sessionId);
    if (statusSubject) {
      return statusSubject.value;
    }

    return null;
  }

  // Enviar archivo a una sesión
  sendFile(sessionId: string, fileData: RemoteCaptureData): void {
    // Si WebSocket no está habilitado, no hacer nada
    if (!environment.websocket?.enabled) {
      return;
    }

    // Conectar WebSocket si es necesario
    this.webSocketService.connectIfNeeded();

    // Intentar usar WebSocket primero
    if (this.webSocketService.isConnected()) {
      this.webSocketService.sendFile(sessionId, 'mobile', fileData);
    }

    // Guardar en localStorage como fallback
    const fileDataStorage = {
      type: 'file',
      fileData: fileData
    };

    localStorage.setItem(this.STORAGE_PREFIX + sessionId + '_file', JSON.stringify(fileDataStorage));

    // También notificar a través de BehaviorSubject
    const session = this.activeSessions.get(sessionId);
    if (session) {
      session.next(fileData);
    }
  }

  // Enviar respuesta de validación al móvil
  sendValidationResponse(computerId: string, validationResult: { success: boolean; message: string }): void {
    // Si WebSocket no está habilitado, no hacer nada
    if (!environment.websocket?.enabled) {
      return;
    }

    // Conectar WebSocket si es necesario
    this.webSocketService.connectIfNeeded();

    // Enviar respuesta de validación a través de WebSocket
    if (this.webSocketService.isConnected()) {
      const message: WebSocketMessage = {
        type: 'validation_response',
        sessionId: computerId, // Usar computerId como sessionId para identificar el móvil
        computerId: 'desktop',
        data: validationResult
      };
      this.webSocketService.sendMessage(message);
    }
  }

    // Terminar una sesión y notificar a todos los clientes
  endSession(sessionId: string): void {
    // SOLO usar WebSocket - no localStorage
    const isConnected = this.webSocketService.isConnected();

    if (isConnected) {
      const message: WebSocketMessage = {
        type: 'session_terminated',
        sessionId: sessionId,
        computerId: 'computer',
        data: {
          message: 'Sesión terminada desde la computadora'
        }
      };
      this.webSocketService.sendMessage(message);
    } else {
      return; // No terminar sesión si no hay WebSocket
    }

    // Limpiar la sesión local
    this.clearSession(sessionId);
  }

  // Limpiar una sesión
  clearSession(sessionId: string): void {
    this.webSocketService.clearFileData();

    const session = this.activeSessions.get(sessionId);
    const statusSession = this.sessionStatus.get(sessionId);

    if (session) {
      session.next(null);
      this.activeSessions.delete(sessionId);
    }

    if (statusSession) {
      statusSession.next({
        active: false,
        message: 'Sesión terminada desde la computadora'
      });
    }

    // Limpiar cualquier dato residual en localStorage
    const storageKey = this.STORAGE_PREFIX + sessionId;
    localStorage.removeItem(storageKey);
    localStorage.removeItem(storageKey + '_file');
  }

  // Limpiar todas las sesiones
  clearAllSessions(): void {
    this.activeSessions.forEach(session => session.next(null));
    this.activeSessions.clear();

    this.sessionStatus.forEach(status => status.next({
      active: false,
      message: 'Sesión terminada'
    }));
    this.sessionStatus.clear();
  }
}
